import { Observable, throwError as observableThrowError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { EventEmitter, Injectable } from '@angular/core';
import { WrapperService } from '../wrapper/wrapper.service';
import { HttpUrlEncodingCodec } from '@angular/common/http';
import { SDSHiercarchicalServiceResult, SDSAutocompleteServiceInterface, SelectionMode, SDSAutocompletelConfiguration } from '@gsa-sam/components';
import { AuthUserService } from '@gsa-sam/shared';
import { OrgDisplayNamePipe } from '../../pipes/fh-display-name/fh-display-name.pipe';
import { FHService } from '../FH/FHService.service';

@Injectable({
  providedIn: 'root',
})
export class AccountDetailsService {
 public orgDataUpdate: EventEmitter<any> = new EventEmitter<any>();
 public userDataUpdate: EventEmitter<any> = new EventEmitter<any>();

  constructor(private wrapperService: WrapperService) {
    // Intentionally blank. Comment is here to satisfy SonarQube
  }

  callApi(
    oApiParam: any,
    convertToJSON: boolean = true,
    queryEncoder: HttpUrlEncodingCodec = null
  ) {
    this.addAuthHeader(oApiParam);
    return this.wrapperService
      ?.call(oApiParam, convertToJSON, queryEncoder)
      ?.pipe(
        catchError((res) => {
          return observableThrowError(res);
        })
      );
  }

  addAuthHeader(options) {
    options.headers = options.headers || {};
  }

  updateUserDetails(body: any) {
    let apiOptions = {
      name: 'rms',
      suffix: `/updateuserprofile`,
      method: 'PUT',
      body: body,
    };
    return this.callApi(apiOptions, false);
  }

  getOrganizationsByIds(ids: string) {
    const oApiParam = {
      name: 'federalHierarchy',
      suffix: '',
      oParam: {
        orgKey: ids,
        sort: 'name',
        mode: 'slim',
      },
      method: 'GET',
    };
    return this.wrapperService.call(oApiParam);
  }

  deactivateAccount(email: string) {
    const oApiParam = {
      name: 'deleteuser',
      suffix: '/' + email + '/',
      method: 'DELETE',
    };
    return this.callApi(oApiParam, false);
  }

  getPublicAPIKeys() {
    const oApiParam = {
      name: 'apikey-get',
      suffix: '',
      method: 'GET',
    };
    return this.wrapperService.call(oApiParam, true, null, true, true);
  }

  removeAPIKey(type) {
    const params = {
      method: 'POST',
      path: '/rms/v1/otp/send',
      oParam: {
        type: type
      }
    };

    return this.wrapperService.call(params, false);
  }

  checkRemoveAPIKeyDisabled(user) {
    const apiOptionsForRemoveButton = {
      method: "GET",
      name: "apiKey24hrsCheck-get",
      oParam: { userId: user?.email },
      suffix: `${user?.email}/`
    };

    return this.wrapperService.call(apiOptionsForRemoveButton);
  }

  public postUserApiKey() {
    const params = {
      name: 'register-apikey',
      method: 'post'
    };
    return this.wrapperService.call(params, false);
  }

  checkPublicAPIKeys(email:string) {
    const oApiParam = {
      name: 'apiKey24hrsCheck-get',
      suffix: `${email}/`,
      method: 'GET',
    };
    return this.wrapperService.call(oApiParam);
  }

  sendOtp() {
    const apiOptions: any = {
      name: 'send-otp',
      oParam: {
        type: 'system_api'
      },
      suffix: '',
      method: 'POST',
    };
    return this.wrapperService.call(apiOptions, true, null, true, true);
  }

  getOtp(otp: string) {
    const oApiParam = {
      name: 'enter-otp',
      suffix: '', oParam: {
        otp
      },
      method: 'GET',
    };
    return this.wrapperService.call(oApiParam, true, null, true, true);
  }

  removeAPIKeys(email: string, otp) {
    const oApiParam = {
      name: 'apikey-remove',
      suffix: email + '/',
      oParam: {
        otp
      },
      method: 'DELETE',
      tempPath: 'apikey-delete'
    };
    return  this.wrapperService.call(oApiParam, false,null,true,true);
  }
}

@Injectable({
  providedIn: 'root',
})
export class SubTierService implements SDSAutocompleteServiceInterface {
  public config: SDSAutocompletelConfiguration =
    new SDSAutocompletelConfiguration();
  private isMultipleLevel = '2';
  private userID = '';
  public mode;

  constructor(
    private fhService: FHService,
    private authUserService: AuthUserService,
    private orgDisplayNamePipe: OrgDisplayNamePipe,
  ) {
    this.config.primaryKeyField = 'key';
    this.config.primaryTextField = 'value';
    this.config.labelText = 'Sub-Tier';
    this.config.selectionMode = SelectionMode.SINGLE;
    this.config.debounceTime = 300;
    this.config.minimumCharacterCountSearch = 3;
    this.config.hideChips = true;
    this.config.secondaryTextField = 'subtext';
    this.userID = this.authUserService.getLoggedInUserId();
  }
  /**
   * Implementation of the getDataByText from SDSAutocompleteServiceInterface it will be triggered by changes to the filters using this service
   * @param currentItems: number of items in the dropdown list
   * @param searchValue: The value/text that has been entered in the filter
   */
  public getDataByText(
    currentItems: number,
    searchValue?: string
  ): Observable<SDSHiercarchicalServiceResult> {
    if (searchValue === "") {
      return of(<SDSHiercarchicalServiceResult>{ items: [], totalItems: 0 });
    } else {
      return this.fhService
        .fhSearch({    q: searchValue,
          pageNum: 1,
          pageSize: 10,
          status: ["active"],
          levels: this.isMultipleLevel,
          orgType: null,
          emailDomain: this.userID,
          isCode: false,
          parent: null,
          isDefaultDept: false,
          depth: 0,
          exclusive: null,
          sort: 'level',
          isNameSearchType: false,
          defaultRoles: false,
          adminHierarchy: false,
          adminRolesHierarchy: '',
          mode: this.mode
        })
        .pipe(
          map((response) => {
            let result: SDSHiercarchicalServiceResult = {
              items: [],
              totalItems: 0,
            };
            let subTierInfo = [];
            response._embedded.forEach((item) => {
              item.org["orgDisplayName"] = this.orgDisplayNamePipe.transform(
                item.org
              );
              subTierInfo.push({
                key: item.org?.orgKey,
                value: `${item.org?.code} - ${item.org?.l2Name}`,
                subtext: `Sub-Tier | Department: ${item.org?.l1ShortName}`
              });
            });
            result.items = subTierInfo;
            result.totalItems = subTierInfo.length;
            return result;
          }),
          catchError((error) => {
            console.log(error);
            return of(<SDSHiercarchicalServiceResult>{
              items: [],
              totalItems: 0,
            });
          })
        );
    }
  }
}
